import React, {useState, useEffect, useRef, useMemo} from 'react';

import useTranslation from '../../hooks/use-translation/use-translation';

import _ from '../../utils/_';
import elementFactory from '../../utils/element';
import Carousel from '../carousel/carousel';
import Arrow from '../icons-af/arrow';
import {
  productGalleryImageSrcSetDef,
  getImageUrl,
  PRODUCT_GALLERY_SIZES,
  DEFAULT_QUALITY,
} from '../../services/image-loader';
import './product-carousel.scss';

const computeImageSize = (containerWidth, containerHeight, imageWidth, imageHeight) => {
  const scaledHeight = (imageHeight * containerWidth) / imageWidth;
  if (scaledHeight <= containerHeight) {
    return {width: containerWidth, height: scaledHeight};
    // return {width: '100%', height: 'auto'};
  } else {
    return {
      width: (imageWidth * containerHeight) / imageHeight,
      height: containerHeight,
    };
    // return {width: 'auto', height: '100%'};
  }
};

const ProductCarousel = ({
  items,
  alt,
  height,
  maxHeight,
  initWidth,
  showIndicators = false,
  onChange = _.noop,
  activeIndex = 0,
  instanceRef = {},
  addSwipeEvent = false,
  clickEvent,
}: propTypes) => {
  const {t} = useTranslation();
  const container = useRef(null);
  const [containerWidth, setContainerWidth] = useState(initWidth);
  const containerHeight = _.def(height) ? height : maxHeight;
  const initIndex = useMemo(() => activeIndex, []);

  useEffect(() => {
    if (container.current === null) {
      return;
    }
    const $ = elementFactory(document, window);
    const measureAndSetWidth = () => {
      setContainerWidth($.innerWidth(container.current));
    };

    measureAndSetWidth();
    const debounced = _.debounce(false, 100, measureAndSetWidth);

    $.on('resize', debounced, window);
    return () => {
      $.off('resize', debounced, window);
    };
  }, [container.current]);

  return (
    <Carousel
      interval={false}
      onChange={onChange}
      instanceRef={instanceRef}
      addSwipeEvent={addSwipeEvent}
    >
      <div className={'carousel carousel-fade carousel-product'}>
        <div className="carousel-inner" ref={container}>
          {items.map((x, i) => (
            <div key={i} className={'carousel-item' + (i === initIndex ? ' active' : '')}>
              <div
                className="carousel-figure-center"
                style={{height: _.def(height) ? height : undefined}}
              >
                <figure
                  style={{
                    backgroundColor: '#f5f4f2',
                    ...computeImageSize(
                      containerWidth,
                      containerHeight,
                      x.retina_width,
                      x.retina_height,
                    ),
                  }}
                >
                  <img
                    loading={i === 0 ? 'eager' : 'lazy'}
                    src={getImageUrl(x.path, 560, 0, DEFAULT_QUALITY)}
                    srcSet={productGalleryImageSrcSetDef(x.path)}
                    sizes={PRODUCT_GALLERY_SIZES}
                    alt={alt}
                    width="100%"
                    height="auto"
                    onClick={clickEvent}
                  />
                </figure>
              </div>
            </div>
          ))}
        </div>
        <a
          className={'carousel-control-prev' + (items.length < 2 ? ' disabled' : '')}
          href="#"
          role="button"
          data-slide="prev"
        >
          <span className="carousel-control-prev-icon" aria-hidden="true">
            <Arrow height="100%" width="30px" rotate={180} />
          </span>
          <span className="sr-only">{t('Previous')}</span>
        </a>
        <a
          className={'carousel-control-next' + (items.length < 2 ? ' disabled' : '')}
          href="#"
          role="button"
          data-slide="next"
        >
          <span className="carousel-control-next-icon" aria-hidden="true">
            <Arrow height="100%" width="30px" />
          </span>
          <span className="sr-only">{t('Next')}</span>
        </a>
        {showIndicators && (
          <ol className="carousel-indicators">
            {items.map((_, i) => (
              <li key={i} data-slide-to={i} className={i === initIndex ? 'active' : ''}></li>
            ))}
          </ol>
        )}
      </div>
    </Carousel>
  );
};

interface propTypes {
  items: any[];
  alt: string;
  height?: number;
  maxHeight?: number;
  initWidth: number;
  showIndicators?: boolean;
  onChange?: (from, to) => any;
  activeIndex?: number;
  instanceRef?: any;
  addSwipeEvent?: boolean;
  clickEvent?: () => any;
}

export default ProductCarousel;
